/* eslint-disable */
/**
 * Integrations — third-party services the brand connects to. Each integration
 * is its own self-contained card with its own credentials, enabled toggle,
 * and save button. Cards POST a partial `integrations` payload to
 * /users/brand/:id; the API sanitizer flattens it to dot-paths so a partial
 * update never wipes a sibling integration's saved keys.
 *
 * Currently shipping: Klaviyo (events + list signup), ShipStation (push
 * redemptions as fulfillment orders).
 *
 * Conventions for new integrations:
 *  - apiKey + apiSecret inputs are masked, never echoed from the API
 *    (schema has select:false). The dashboard relies on the
 *    `has<Provider>ApiKey` boolean returned by GET /users/brand/:id to
 *    show the saved/unsaved status badge.
 *  - Submitting an empty apiKey input means "leave the saved key alone"
 *    (UX: a fresh page load shouldn't accidentally wipe creds).
 *  - The card never sends a partial `integrations` blob other than its
 *    own slice — keeps cross-card writes isolated.
 */
import { useEffect, useState } from "react";
import api from "api";
import { toast } from "react-toastify";
import {
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Badge,
} from "reactstrap";
import { Store, UpdateStore } from "StoreContext";

const cardStyle = {
  borderRadius: 12,
  border: "none",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  marginBottom: 20,
  fontFamily: "Outfit",
};

const StatusDot = ({ on }) => (
  <span
    aria-hidden
    style={{
      display: "inline-block",
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: on ? "#00B207" : "#cbd5e1",
      marginRight: 6,
    }}
  />
);

// =============================================================================
// Klaviyo
// =============================================================================
const KlaviyoCard = () => {
  const store = Store();
  const updateStore = UpdateStore();
  const { user } = store || {};

  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [listId, setListId] = useState("");
  const [hasSavedKey, setHasSavedKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    const k = user?.integrations?.klaviyo || {};
    setEnabled(!!k.enabled);
    setListId(k.listId || "");
    setApiKey("");
    setHasSavedKey(
      typeof user?.hasKlaviyoApiKey === "boolean"
        ? user.hasKlaviyoApiKey
        : !!k.enabled
    );
  }, [user]);

  const save = async () => {
    if (!user?._id) return;
    setSaving(true);
    try {
      const payload = {
        enabled: !!enabled,
        listId: listId.trim().slice(0, 64),
      };
      // Only send apiKey when the admin typed something. Empty input + saved
      // key on file = leave the saved key alone.
      if (apiKey.trim()) payload.apiKey = apiKey.trim();
      const fd = new FormData();
      fd.append("integrations", JSON.stringify({ klaviyo: payload }));
      const res = await api("put", `/users/brand/${user._id}`, fd);
      if (res?.success) {
        toast.success("Klaviyo integration saved.");
        if (res.user && updateStore) updateStore({ user: res.user });
        setApiKey("");
      } else {
        toast.error(res?.message || "Failed to save Klaviyo settings.");
      }
    } catch {
      // axios interceptor handles toast
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    if (testing) return;
    if (apiKey.trim()) {
      toast.info("Save your changes before testing — the test uses the saved key.");
      return;
    }
    if (!hasSavedKey) {
      toast.error("Save an API key first, then test.");
      return;
    }
    setTesting(true);
    try {
      const res = await api("post", "/users/integrations/klaviyo/test");
      if (res?.success) {
        toast.success("Klaviyo accepted the test event. Check your account's Activity feed.");
      } else {
        toast.error(res?.message || "Klaviyo rejected the test event.");
      }
    } catch {
      // axios interceptor handles toast
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card style={cardStyle}>
      <CardHeader style={{ background: "transparent", borderBottom: "1px solid #f1f1f5", padding: "16px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1C1C1C" }}>
              <StatusDot on={enabled && hasSavedKey} />
              Klaviyo
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6B7280" }}>
              Stream scanner events (sign-up, scan, redemption, sign-in) into Klaviyo for flows + segmentation.
            </p>
          </div>
          {hasSavedKey && (
            <Badge color="success" pill style={{ fontWeight: 600 }}>
              API key saved
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardBody style={{ padding: "16px 20px 20px" }}>
        <Form>
          <FormGroup check style={{ marginBottom: 14 }}>
            <Label check style={{ fontSize: 14 }}>
              <Input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />{" "}
              Enable Klaviyo events
            </Label>
          </FormGroup>
          <FormGroup>
            <Label style={{ fontSize: 13, fontWeight: 600 }}>
              Private API key {hasSavedKey && <span style={{ color: "#9ca3af", fontWeight: 400 }}>· (leave blank to keep saved)</span>}
            </Label>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={hasSavedKey ? "••••••••" : "pk_xxxxxxxx"}
              autoComplete="off"
            />
            <small style={{ color: "#6B7280" }}>
              Find this in Klaviyo → Account → Settings → API Keys → Private API keys.
            </small>
          </FormGroup>
          <FormGroup>
            <Label style={{ fontSize: 13, fontWeight: 600 }}>
              Default list ID <span style={{ color: "#9ca3af", fontWeight: 400 }}>· optional</span>
            </Label>
            <Input
              type="text"
              value={listId}
              onChange={(e) => setListId(e.target.value)}
              placeholder="Wf3xYz"
              maxLength={64}
            />
            <small style={{ color: "#6B7280" }}>
              New scanners are added to this list on first scan. Leave blank to skip list signup.
            </small>
          </FormGroup>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <Button color="secondary" outline onClick={test} disabled={testing || saving}>
              {testing ? <><Spinner size="sm" /> Testing…</> : "Test connection"}
            </Button>
            <Button color="primary" onClick={save} disabled={saving}>
              {saving ? <><Spinner size="sm" /> Saving…</> : "Save Klaviyo"}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
};

// =============================================================================
// ShipStation
// =============================================================================
const ShipStationCard = () => {
  const store = Store();
  const updateStore = UpdateStore();
  const { user } = store || {};

  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [storeId, setStoreId] = useState("");
  const [hasSavedCreds, setHasSavedCreds] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const s = user?.integrations?.shipstation || {};
    setEnabled(!!s.enabled);
    setStoreId(s.storeId || "");
    setApiKey("");
    setApiSecret("");
    setHasSavedCreds(
      typeof user?.hasShipstationApiKey === "boolean"
        ? user.hasShipstationApiKey
        : !!s.enabled
    );
  }, [user]);

  const save = async () => {
    if (!user?._id) return;
    setSaving(true);
    try {
      const payload = {
        enabled: !!enabled,
        storeId: storeId.trim().slice(0, 64),
      };
      if (apiKey.trim()) payload.apiKey = apiKey.trim();
      if (apiSecret.trim()) payload.apiSecret = apiSecret.trim();
      const fd = new FormData();
      fd.append("integrations", JSON.stringify({ shipstation: payload }));
      const res = await api("put", `/users/brand/${user._id}`, fd);
      if (res?.success) {
        toast.success("ShipStation integration saved.");
        if (res.user && updateStore) updateStore({ user: res.user });
        setApiKey("");
        setApiSecret("");
      } else {
        toast.error(res?.message || "Failed to save ShipStation settings.");
      }
    } catch {
      // axios interceptor handles toast
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={cardStyle}>
      <CardHeader style={{ background: "transparent", borderBottom: "1px solid #f1f1f5", padding: "16px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1C1C1C" }}>
              <StatusDot on={enabled && hasSavedCreds} />
              ShipStation
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6B7280" }}>
              Push redemptions to ShipStation as orders so fulfillment uses your existing labels + workflows.
            </p>
          </div>
          {hasSavedCreds && (
            <Badge color="success" pill style={{ fontWeight: 600 }}>
              Credentials saved
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardBody style={{ padding: "16px 20px 20px" }}>
        <Form>
          <FormGroup check style={{ marginBottom: 14 }}>
            <Label check style={{ fontSize: 14 }}>
              <Input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />{" "}
              Push redemptions to ShipStation
            </Label>
          </FormGroup>
          <FormGroup>
            <Label style={{ fontSize: 13, fontWeight: 600 }}>
              API key {hasSavedCreds && <span style={{ color: "#9ca3af", fontWeight: 400 }}>· (leave blank to keep saved)</span>}
            </Label>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={hasSavedCreds ? "••••••••" : "API key"}
              autoComplete="off"
            />
          </FormGroup>
          <FormGroup>
            <Label style={{ fontSize: 13, fontWeight: 600 }}>
              API secret {hasSavedCreds && <span style={{ color: "#9ca3af", fontWeight: 400 }}>· (leave blank to keep saved)</span>}
            </Label>
            <Input
              type="password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              placeholder={hasSavedCreds ? "••••••••" : "API secret"}
              autoComplete="off"
            />
            <small style={{ color: "#6B7280" }}>
              Generate from ShipStation → Account Settings → API Settings.
            </small>
          </FormGroup>
          <FormGroup>
            <Label style={{ fontSize: 13, fontWeight: 600 }}>
              Default store ID <span style={{ color: "#9ca3af", fontWeight: 400 }}>· optional</span>
            </Label>
            <Input
              type="text"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              placeholder="123456"
              maxLength={64}
            />
            <small style={{ color: "#6B7280" }}>
              Pushed orders are attached to this store. Leave blank to use ShipStation's default store.
            </small>
          </FormGroup>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button color="primary" onClick={save} disabled={saving}>
              {saving ? <><Spinner size="sm" /> Saving…</> : "Save ShipStation"}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
};

const Integrations = () => (
  <div>
    <ShipStationCard />
    <KlaviyoCard />
  </div>
);

export default Integrations;
